import { Box, BoxProps, Flex, Link, Text } from "@chakra-ui/react";
import { RiFlashlightLine, RiMenuSearchLine } from "@remixicon/react";
import { createColumnHelper } from "@tanstack/react-table";
import { useMemo, useState } from "react";
import { Link as RouterLink } from "react-router";

import { SidebarEventDetails } from "@/app/components/DebugEventDetails";
import { useDebugEventsData } from "@/app/data/useDebugEventsData";
import AutocompleteSelect, {
  getStringFilterFunction,
  getStringRenderFunction,
} from "@/common/components/Autocomplete/AutocompleteSelect";
import { CompanyAutocompleteSelect } from "@/common/components/Autocomplete/CompanyAutocompleteSelect";
import { FeatureAutocompleteSelect } from "@/common/components/Autocomplete/FeatureAutocompleteSelect";
import { UserAutocompleteSelect } from "@/common/components/Autocomplete/UserAutocompleteSelect";
import CompanyDisplay from "@/common/components/CompanyDisplay";
import { DataTable } from "@/common/components/DataTable/DataTable";
import {
  DebugEventTypeDisplay,
  DebugEventTypeSelect,
} from "@/common/components/DebugEventTypeSelect";
import { DocumentationRefButton } from "@/common/components/DocumentationReference";
import EmptyState from "@/common/components/EmptyState";
import { EventCell } from "@/common/components/EventCell";
import {
  IcoFeature,
  IcoOrganization,
  IcoSearch,
  IcoTrackingEvent,
  IcoUser,
} from "@/common/components/Icons";
import InfiniteAnimation from "@/common/components/InfiniteAnimation";
import { WarningBadgeTooltip } from "@/common/components/InfoIconTooltip";
import NotAvailableCell from "@/common/components/NotAvailableCell";
import SectionDescription from "@/common/components/SectionDescription";
import { SectionHeading } from "@/common/components/SectionHeading";
import SidebarLayout, {
  SidebarContainer,
  SidebarSection,
} from "@/common/components/SidebarLayout";
import { SwitchButtonGroup } from "@/common/components/SwitchButtonGroup";
import TimestampCell from "@/common/components/TimestampCell";
import { UserDisplay } from "@/common/components/UserDisplay";
import { useEnvironment } from "@/common/contexts/EnvironmentContext";
import { useEventNames } from "@/common/data/useEventNames";
import {
  useSearchArrayParam,
  useSearchParam,
} from "@/common/hooks/useSearchParam";
import { useSidebar } from "@/common/hooks/useSidebar";
import { FeatureDisplay } from "@/feature/components/FeatureDisplay";
import {
  DebugEvent,
  DebugEventType,
  DebugEventTypes,
} from "@reflag/shared/eventAPI";
import { GlobalSettingsUrl } from "@reflag/shared/urls";

const DAYS_HISTORY = 3;

export const cellVerticalAdjust: BoxProps = {
  display: "inline-flex",
  alignItems: "center",
  minH: 7,
};

function SidebarInstructions() {
  const { env } = useEnvironment();
  return (
    <SidebarContainer>
      <SidebarSection>
        <SectionHeading>Getting started guide</SectionHeading>
        <SectionDescription>
          Use Segment (several languages) or the Reflag SDK (JS) to send data to
          Reflag.
        </SectionDescription>
        <Box>
          <DocumentationRefButton fullSize />
        </Box>
      </SidebarSection>
      <SidebarSection>
        <SectionHeading>SDK & API keys</SectionHeading>
        <Text color="dimmed" fontSize="sm" maxWidth="2xl">
          You can find the publishable and secret keys for each environment in{" "}
          <Link as={RouterLink} to={GlobalSettingsUrl(env, "org-api-access")}>
            SDK & API keys
          </Link>
        </Text>
      </SidebarSection>
    </SidebarContainer>
  );
}

export default function DebuggerRecent() {
  const { env } = useEnvironment();
  const [shouldRefetch, setShouldRefetch] = useState<boolean>(true);

  const [eventName, setEventName] = useSearchParam("search");
  const [companyId, setCompanyId] = useSearchParam("company");
  const [userId, setUserId] = useSearchParam("user");
  const [featureId, setFeatureId] = useSearchParam("feature");
  const [eventTypes, setEventTypes] =
    useSearchArrayParam<DebugEventType>("eventTypes");
  const { data: eventNames = [] } = useEventNames();
  const { show: showSidebar } = useSidebar("debugger");

  const validEventTypes = useMemo(
    () => eventTypes.filter((type) => DebugEventTypes.includes(type)),
    [eventTypes],
  );
  const eventNameSuggestions = useMemo(
    () => eventNames.map((name) => ({ id: name })),
    [eventNames],
  );
  const selectedEventName = useMemo(
    () =>
      eventName
        ? eventNameSuggestions.find((item) => item.id === eventName) || {
            id: eventName,
          }
        : undefined,
    [eventName, eventNameSuggestions],
  );

  const { data: events = [], isLoading } = useDebugEventsData(
    { eventName, companyId, userId, featureId, eventTypes: validEventTypes },
    { live: shouldRefetch },
  );
  const [activeEvent, setActiveEvent] = useState<DebugEvent | null>(null);
  const columnHelper = createColumnHelper<DebugEvent>();
  const columns = useMemo(
    () => [
      columnHelper.accessor("timestamp", {
        header: "Received",
        cell: (cell) => {
          const value = cell.getValue();
          return (
            <Box {...cellVerticalAdjust}>
              {value !== undefined ? (
                <TimestampCell value={value} autoUpdate leftAlign />
              ) : (
                <NotAvailableCell />
              )}
            </Box>
          );
        },
      }),
      columnHelper.accessor("debugEventType", {
        header: "Type",
        cell: (cell) => {
          const value = cell.getValue();
          return (
            <Box {...cellVerticalAdjust}>
              {value !== undefined ? (
                <DebugEventTypeDisplay fontSize="sm" type={value} />
              ) : (
                <NotAvailableCell />
              )}
            </Box>
          );
        },
      }),
      columnHelper.display({
        header: "Event",
        cell: (cell) => (
          <Box {...cellVerticalAdjust}>
            <EventCell event={cell.row.original} />
          </Box>
        ),
      }),
      columnHelper.accessor("features", {
        header: "Flag",
        cell: (cell) => (
          <Box {...cellVerticalAdjust}>
            {cell.getValue().length === 0 && <NotAvailableCell />}
            {cell.getValue().length === 1 && (
              <FeatureDisplay
                feature={cell.getValue()[0]}
                fontSize="sm"
                includeParents={false}
                link
              />
            )}
            {cell.getValue().length > 1 && (
              <Text color="dimmed" fontSize="sm">
                {cell.getValue().length} flags
              </Text>
            )}
          </Box>
        ),
      }),
      columnHelper.accessor("company", {
        header: "Company",
        minSize: 150,
        cell: (cell) => {
          const company = cell.getValue();
          return (
            <Box {...cellVerticalAdjust}>
              {company ? (
                <CompanyDisplay
                  company={company}
                  fontSize="sm"
                  link={company.known}
                />
              ) : (
                <Flex alignItems="center" fontSize="sm" gap={1}>
                  {cell.row.original.debugEventType != "user" ? (
                    <WarningBadgeTooltip
                      label="Missing"
                      text="Company ID is missing from this event. Client is possibly set up incorrectly."
                    />
                  ) : (
                    <NotAvailableCell />
                  )}
                </Flex>
              )}
            </Box>
          );
        },
      }),
      columnHelper.accessor("user", {
        header: "User",
        minSize: 150,
        cell: (cell) => {
          const user = cell.getValue();
          return (
            <Box {...cellVerticalAdjust}>
              {user ? (
                <UserDisplay fontSize="sm" link user={user} />
              ) : (
                <NotAvailableCell />
              )}
            </Box>
          );
        },
      }),
    ],
    [columnHelper],
  );

  return (
    <SidebarLayout
      flexGrow={1}
      sidebarContent={
        activeEvent !== null ? (
          <SidebarEventDetails
            event={activeEvent}
            onClose={() => setActiveEvent(null)}
          />
        ) : (
          <SidebarInstructions />
        )
      }
      sidebarKey="debugger"
      templateColumns="repeat(3, 1fr) clamp(230px, 20%, 400px)"
    >
      <DataTable
        _toolbarLeft={{ wrap: "wrap", alignSelf: "flex-start" }}
        _toolbarRight={{ wrap: "wrap", flex: "1 1 min-content" }}
        columns={columns}
        data={events}
        emptyState={
          <EmptyState
            description={
              <>
                Events will show up here as soon as they come in. Note: tracking
                data is{" "}
                <Link
                  as={RouterLink}
                  to={GlobalSettingsUrl(env, "app-environments")}
                >
                  environment
                </Link>{" "}
                specific!
              </>
            }
            h="100%"
            icon={<RiMenuSearchLine size={48} />}
            isLoading={isLoading}
            title={`No events the past ${DAYS_HISTORY} days`}
          />
        }
        highlightRow={(row) => row.original.id === activeEvent?.id}
        isFetching={isLoading}
        sorting={[]}
        tableId="debug-events"
        toolbarLeftActions={() => (
          <>
            <DebugEventTypeSelect
              leftIcon={<IcoSearch color="dimmed" size="sm" />}
              placeholder="Any event type"
              value={validEventTypes}
              onChange={setEventTypes}
            />
            <CompanyAutocompleteSelect
              leftIcon={<IcoOrganization color="dimmed" size="sm" />}
              maxW="lg"
              placeholder="Any company"
              value={companyId}
              canClear
              valueAsId
              onChange={setCompanyId}
            />
            <UserAutocompleteSelect
              leftIcon={<IcoUser color="dimmed" size="sm" />}
              maxW="lg"
              placeholder="Any user"
              value={userId}
              canClear
              valueAsId
              onChange={setUserId}
            />
            <FeatureAutocompleteSelect
              leftIcon={<IcoFeature color="dimmed" size="sm" />}
              maxW="lg"
              placeholder="Any flag"
              value={featureId}
              canClear
              valueAsId
              onChange={setFeatureId}
            />
            <AutocompleteSelect
              canClear
              itemFilterFn={getStringFilterFunction("id")}
              itemKeyProperty="id"
              itemRenderFn={getStringRenderFunction("id")}
              leftIcon={<IcoTrackingEvent color="dimmed" size="sm" />}
              maxW="xs"
              placeholder="Any event"
              suggestions={eventNameSuggestions}
              value={selectedEventName}
              onChange={(nextValue) => setEventName(nextValue?.id || undefined)}
            />
          </>
        )}
        toolbarRightActions={() => (
          <SwitchButtonGroup
            buttons={[
              {
                id: "live",
                label: "Live",
                leftIcon: shouldRefetch ? (
                  <InfiniteAnimation>
                    <RiFlashlightLine size={15} />
                  </InfiniteAnimation>
                ) : (
                  <RiFlashlightLine size={15} />
                ),
              },
              { id: "paused", label: "Paused" },
            ]}
            value={shouldRefetch ? "live" : "paused"}
            onChange={(value) => setShouldRefetch(value === "live")}
          />
        )}
        variant="clickable"
        onRowClick={(row) => {
          showSidebar();
          setActiveEvent(row.original);
        }}
      />
    </SidebarLayout>
  );
}
